import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const InstallPromptContext = createContext({
  isInstallable: false,
  promptInstall: async () => ({ outcome: "dismissed" }),
  prompting: false,
  isIos: false,
  isStandalone: false,
});

function detectIos() {
  if (typeof window === "undefined") return false;
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");
}

function detectStandalone() {
  if (typeof window === "undefined") return false;
  const matchMediaSupported = typeof window.matchMedia === "function";
  const displayModeStandalone = matchMediaSupported
    ? window.matchMedia("(display-mode: standalone)").matches
    : false;
  return displayModeStandalone || window.navigator.standalone === true;
}

export function InstallPromptProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setInstallable] = useState(false);
  const [prompting, setPrompting] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    setIsIos(detectIos());
    setIsStandalone(detectStandalone());

    let mediaQuery;
    const handleDisplayModeChange = (event) => {
      setIsStandalone(event.matches || window.navigator.standalone === true);
    };

    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      mediaQuery = window.matchMedia("(display-mode: standalone)");
      if (mediaQuery?.addEventListener) {
        mediaQuery.addEventListener("change", handleDisplayModeChange);
      } else if (mediaQuery?.addListener) {
        mediaQuery.addListener(handleDisplayModeChange);
      }
    }

    return () => {
      if (!mediaQuery) return;
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleDisplayModeChange);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(handleDisplayModeChange);
      }
    };
  }, []);

  useEffect(() => {
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setInstallable(true);
    };

    const onAppInstalled = () => {
      setDeferredPrompt(null);
      setInstallable(false);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { outcome: "unavailable" };

    setPrompting(true);
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setInstallable(false);
      return choice;
    } finally {
      setPrompting(false);
    }
  }, [deferredPrompt]);

  const value = useMemo(
    () => ({
      isInstallable,
      promptInstall,
      prompting,
      isIos,
      isStandalone,
    }),
    [isInstallable, promptInstall, prompting, isIos, isStandalone]
  );

  return (
    <InstallPromptContext.Provider value={value}>
      {children}
    </InstallPromptContext.Provider>
  );
}

export function useInstallPrompt() {
  return useContext(InstallPromptContext);
}


